import { useState, useEffect, useRef, useCallback } from 'react';
import { apiGet, apiPost, apiPut, apiDelete, Store, Room, Notice, Pricing, StoreSettings } from '../../../api';
import { NoticeCarouselAdmin } from '../../NoticeCarousel';

interface StoreSettingsTabProps {
  store: Store;
  onUpdate?: () => void;
}

interface OperatingHoursApi {
  id?: number;
  store_id?: string;
  day_of_week: number;
  excluded_start: string | null;  // 예약 제외 시작 시간
  excluded_end: string | null;    // 예약 제외 종료 시간
  is_closed: boolean;
}

const DAY_NAMES = ['일', '월', '화', '수', '목', '금', '토'];

// 시간 옵션 (00 ~ 23)
const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0'));

// 종료 시간 옵션 (00 ~ 24) - 24:00 = 자정 = 하루의 끝
const HOUR_OPTIONS_END = [...HOUR_OPTIONS, '24'];

// 분 옵션 (00, 10, 20, 30, 40, 50)
const MINUTE_OPTIONS = Array.from({ length: 6 }, (_, i) => (i * 10).toString().padStart(2, '0'));

// 시간 문자열에서 시간/분 추출
const parseTimeString = (time: string | null): { hour: string; minute: string } => {
  if (!time) return { hour: '09', minute: '00' };  // 기본값: 제외 시작 09:00
  const [hour, minute] = time.split(':');
  // 분을 10분 단위로 반올림
  const roundedMinute = (Math.round(parseInt(minute || '0') / 10) * 10) % 60;
  return {
    hour: hour || '09',
    minute: roundedMinute.toString().padStart(2, '0'),
  };
};

// 시간/분을 합쳐서 문자열로
const combineTime = (hour: string, minute: string): string => `${hour}:${minute}`;

export default function StoreSettingsTab({ store, onUpdate }: StoreSettingsTabProps) {
  // 동기화 필요 여부 계산
  const needsOperationalSync = !!(store.operational_modified_at && (
    !store.kiosk_status?.last_operational_sync_at ||
    new Date(store.operational_modified_at) > new Date(store.kiosk_status.last_operational_sync_at)
  ));

  // 운영 데이터 동기화
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncMessage, setSyncMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const handleSyncOperational = async () => {
    try {
      setSyncLoading(true);
      setSyncMessage(null);
      const response = await apiPost(`/api/v1/stores/${store.id}/sync/operational`, {});
      if (!response.success) throw new Error(response.error);
      setSyncMessage({ type: 'success', text: '키오스크에 동기화 알림을 전송했습니다.' });
      // 키오스크가 데이터를 가져간 후 타임스탬프가 갱신되므로 잠시 후 새로고침
      setTimeout(() => {
        onUpdate?.();
        setSyncMessage(null);
      }, 3000);
    } catch (error: any) {
      setSyncMessage({ type: 'error', text: `동기화 실패: ${error.message || '알 수 없는 오류'}` });
      setTimeout(() => setSyncMessage(null), 5000);
    } finally {
      setSyncLoading(false);
    }
  };

  // 섹션 펼침 상태
  const [expandedSections, setExpandedSections] = useState({
    basicInfo: false,
    pricing: false,
    operatingHours: false,
    closedDates: false,
    notices: false,
    rooms: false,
  });

  // 기본 정보 상태
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>(store.image || '');
  const [storeDescription, setStoreDescription] = useState<string>(store.description || '');
  const [imageUploading, setImageUploading] = useState(false);
  const imageInputRef = useRef<HTMLInputElement>(null);

  // 요금 설정 상태
  const [pricing, setPricing] = useState<Pricing[]>([]);
  const [pricingLoading, setPricingLoading] = useState(true);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [editingPricing, setEditingPricing] = useState<Pricing | null>(null);
  const [pricingForm, setPricingForm] = useState({
    display_name: '',
    price_per_hour: 0,
    day_type: 'all' as 'all' | 'weekday' | 'weekend' | 'holiday' | 'custom',
    time_slot: 'all_day' as 'all_day' | 'daytime' | 'nighttime',
    start_hour: 0,
    end_hour: 24,
  });

  // 운영시간 상태
  const [operatingHours, setOperatingHours] = useState<OperatingHoursApi[]>([]);
  const [operatingHoursLoading, setOperatingHoursLoading] = useState(true);
  const [operatingHoursSaving, setOperatingHoursSaving] = useState(false);
  const [storeHoursText, setStoreHoursText] = useState<string>(store.hours || '');  // 운영시간 안내 문구

  // 시간대 구분 설정 상태 (요금제에서 사용)
  const [daytimeSettings, setDaytimeSettings] = useState({
    daytime_enabled: true,  // 주간/야간 구분 사용 여부
    daytime_start_hour: 6,
    daytime_end_hour: 18,
  });
  const [daytimeSettingsSaving, setDaytimeSettingsSaving] = useState(false);

  // 게임 라이프사이클 설정
  const [preWarmMinutes, setPreWarmMinutes] = useState(10);
  const [preWarmSaving, setPreWarmSaving] = useState(false);

  // 특정일 휴무 상태
  interface ClosedDateItem {
    id: number;
    store_id: string;
    start_date: string;
    start_time: string | null;
    end_date: string;
    end_time: string | null;
    reason: string | null;
    created_at: string;
  }
  const [closedDates, setClosedDates] = useState<ClosedDateItem[]>([]);
  const [closedDatesLoading, setClosedDatesLoading] = useState(true);
  const [closedDateForm, setClosedDateForm] = useState({
    start_date: '', start_time: '',
    end_date: '', end_time: '',
    reason: '',
  });
  const [closedDateAdding, setClosedDateAdding] = useState(false);
  const closedDateStartRef = useRef<HTMLInputElement>(null);
  const closedDateEndRef = useRef<HTMLInputElement>(null);

  // 공지사항 상태
  const [notices, setNotices] = useState<Notice[]>([]);
  const [noticesLoading, setNoticesLoading] = useState(true);
  const [showNoticeModal, setShowNoticeModal] = useState(false);
  const [editingNotice, setEditingNotice] = useState<Notice | null>(null);
  const [noticeForm, setNoticeForm] = useState({ title: '', content: '' });

  // 룸 설정 상태
  const [rooms, setRooms] = useState<Room[]>([]);
  const [roomsLoading, setRoomsLoading] = useState(true);
  const [roomImageUploading, setRoomImageUploading] = useState<string | null>(null);
  const roomImageInputRef = useRef<HTMLInputElement>(null);
  const [editingRoomId, setEditingRoomId] = useState<string | null>(null);
  const [dirtyRoomIds, setDirtyRoomIds] = useState<Set<string>>(new Set());

  const markRoomDirty = (roomId: string) => {
    setDirtyRoomIds(prev => new Set(prev).add(roomId));
  };

  // 룸 배치도 상태
  const [roomLayoutImage, setRoomLayoutImage] = useState<string>(store.room_layout_image || '');
  const [roomLayoutUploading, setRoomLayoutUploading] = useState(false);
  const roomLayoutInputRef = useRef<HTMLInputElement>(null);

  // 데이터 로드
  useEffect(() => {
    loadPricing();
    loadOperatingHours();
    loadClosedDates();
    loadNotices();
    loadStoreSettings();
    loadRooms();
  }, [store.id]);

  useEffect(() => {
    setImagePreview(store.image || '');
    setStoreDescription(store.description || '');
    setStoreHoursText(store.hours || '');
  }, [store]);

  // StoreSettings 로드 (시간대 구분 설정)
  const loadStoreSettings = async () => {
    try {
      const response = await apiGet<StoreSettings>(`/api/v1/stores/${store.id}/settings`);
      if (response.success && response.data) {
        setDaytimeSettings({
          daytime_enabled: response.data.daytime_enabled ?? true,
          daytime_start_hour: response.data.daytime_start_hour ?? 6,
          daytime_end_hour: response.data.daytime_end_hour ?? 18,
        });
        setPreWarmMinutes(response.data.pre_warm_minutes ?? 10);
      }
    } catch (error) {
      console.error('Error loading store settings:', error);
    }
  };

  // 시간대 구분 설정 저장
  const handleSaveDaytimeSettings = async () => {
    try {
      setDaytimeSettingsSaving(true);
      const response = await apiPut(`/api/v1/stores/${store.id}/settings`, daytimeSettings);
      if (!response.success) throw new Error(response.error);
      alert('시간대 설정이 저장되었습니다.');
    } catch (error: any) {
      alert(`저장 실패: ${error.message || '알 수 없는 오류'}`);
    } finally {
      setDaytimeSettingsSaving(false);
    }
  };

  // 사전 준비 시간 저장
  const handleSavePreWarmMinutes = async () => {
    try {
      setPreWarmSaving(true);
      const response = await apiPut(`/api/v1/stores/${store.id}/settings`, { pre_warm_minutes: preWarmMinutes });
      if (!response.success) throw new Error(response.error);
      alert('사전 준비 시간이 저장되었습니다.');
    } catch (error: any) {
      alert(`저장 실패: ${error.message || '알 수 없는 오류'}`);
    } finally {
      setPreWarmSaving(false);
    }
  };

  // ==================== 기본 정보 ====================
  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        alert('이미지 파일만 업로드 가능합니다.');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        alert('파일 크기는 5MB 이하여야 합니다.');
        return;
      }
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result as string);
      reader.readAsDataURL(file);
    }
  };

  const uploadStoreImage = async (file: File): Promise<string | null> => {
    const token = localStorage.getItem('auth_token');
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(`/api/v1/upload?type=stores`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const result = await response.json();
      if (result.success && result.data?.url) return result.data.url;
      throw new Error(result.error || '업로드 실패');
    } catch (error) {
      console.error('Upload error:', error);
      return null;
    }
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview('');
    if (imageInputRef.current) imageInputRef.current.value = '';
  };

  const handleSaveBasicInfo = async () => {
    try {
      setImageUploading(true);
      let imageUrl = imagePreview;

      if (imageFile) {
        const uploadedUrl = await uploadStoreImage(imageFile);
        if (!uploadedUrl) {
          alert('이미지 업로드에 실패했습니다.');
          return;
        }
        imageUrl = uploadedUrl;
      } else if (imagePreview && !imagePreview.startsWith('/uploads/') && !imagePreview.startsWith('http')) {
        imageUrl = '';
      }

      const response = await apiPut(`/api/v1/stores/${store.id}`, {
        image: imageUrl || null,
        description: storeDescription || null,
      });

      if (!response.success) throw new Error(response.error);

      setImageFile(null);
      if (imageInputRef.current) imageInputRef.current.value = '';
      onUpdate?.();
      alert('매장 정보가 저장되었습니다.');
    } catch (error: any) {
      console.error('Error saving store info:', error);
      alert(`저장 실패: ${error.message || '알 수 없는 오류'}`);
    } finally {
      setImageUploading(false);
    }
  };

  // ==================== 요금 설정 ====================
  const loadPricing = async () => {
    try {
      setPricingLoading(true);
      const response = await apiGet<Pricing[]>(`/api/v1/pricing?store_id=${store.id}`);
      if (response.success && response.data) setPricing(response.data);
    } catch (error) {
      console.error('Error loading pricing:', error);
    } finally {
      setPricingLoading(false);
    }
  };

  const handleAddPricing = () => {
    setEditingPricing(null);
    setPricingForm({
      display_name: '',
      price_per_hour: 0,
      day_type: 'all',
      time_slot: 'all_day',
      start_hour: 0,
      end_hour: 24,
    });
    setShowPricingModal(true);
  };

  const handleEditPricing = (item: Pricing) => {
    setEditingPricing(item);
    setPricingForm({
      display_name: item.display_name || '',
      price_per_hour: item.price_per_hour,
      day_type: (item.day_type as 'all' | 'weekday' | 'weekend' | 'holiday' | 'custom') || 'all',
      time_slot: (item.time_slot as 'all_day' | 'daytime' | 'nighttime') || 'all_day',
      start_hour: item.start_hour || 0,
      end_hour: item.end_hour || 24,
    });
    setShowPricingModal(true);
  };

  const handleSubmitPricing = async (e: React.FormEvent) => {
    e.preventDefault();

    // 시간대 자동 설정 (매장별 시간대 구분 설정 사용)
    let { start_hour, end_hour } = pricingForm;
    if (pricingForm.time_slot === 'all_day') {
      start_hour = 0;
      end_hour = 24;
    } else if (pricingForm.time_slot === 'daytime') {
      start_hour = daytimeSettings.daytime_start_hour;
      end_hour = daytimeSettings.daytime_end_hour;
    } else if (pricingForm.time_slot === 'nighttime') {
      start_hour = daytimeSettings.daytime_end_hour;
      end_hour = daytimeSettings.daytime_start_hour; // 다음날 주간 시작까지
    }

    try {
      const payload = {
        store_id: store.id,
        display_name: pricingForm.display_name,
        price_per_hour: pricingForm.price_per_hour,
        day_type: pricingForm.day_type,
        time_slot: pricingForm.time_slot,
        start_hour,
        end_hour,
      };

      const response = editingPricing
        ? await apiPut(`/api/v1/pricing/${editingPricing.id}`, payload)
        : await apiPost('/api/v1/pricing', payload);

      if (!response.success) throw new Error(response.error);
      setShowPricingModal(false);
      loadPricing();
      onUpdate?.();
    } catch (error: any) {
      alert(`저장 실패: ${error.message || '알 수 없는 오류'}`);
    }
  };

  const handleDeletePricing = async (pricingId: string) => {
    if (!confirm('정말 이 요금제를 삭제하시겠습니까?')) return;
    try {
      const response = await apiDelete(`/api/v1/pricing/${pricingId}`);
      if (!response.success) throw new Error(response.error);
      loadPricing();
      onUpdate?.();
    } catch (error: any) {
      alert(`삭제 실패: ${error.message || '알 수 없는 오류'}`);
    }
  };

  // ==================== 운영시간 ====================
  // 시간 문자열을 HH:MM 형식으로 정규화 (초 제거)
  const normalizeTime = (time: string | null): string | null => {
    if (!time) return null;
    // "22:00:00" -> "22:00" 또는 "22:00" -> "22:00"
    const match = time.match(/^(\d{2}):(\d{2})/);
    return match ? `${match[1]}:${match[2]}` : time;
  };

  const loadOperatingHours = async () => {
    try {
      setOperatingHoursLoading(true);
      const response = await apiGet<OperatingHoursApi[]>(`/api/v1/stores/${store.id}/operating-hours`);
      if (response.success && response.data && response.data.length > 0) {
        // 시간 형식 정규화
        const normalized = response.data.map((h) => ({
          ...h,
          excluded_start: normalizeTime(h.excluded_start),
          excluded_end: normalizeTime(h.excluded_end),
        }));
        setOperatingHours(normalized);
      } else {
        // 기본값 설정: 제외 시간 없음 (24시간 예약 가능)
        setOperatingHours(
          Array.from({ length: 7 }, (_, i) => ({
            day_of_week: i,
            excluded_start: null,  // null = 제외 시간 없음
            excluded_end: null,
            is_closed: false,
          }))
        );
      }
    } catch (error) {
      console.error('Error loading operating hours:', error);
      // 기본값 설정: 제외 시간 없음 (24시간 예약 가능)
      setOperatingHours(
        Array.from({ length: 7 }, (_, i) => ({
          day_of_week: i,
          excluded_start: null,
          excluded_end: null,
          is_closed: false,
        }))
      );
    } finally {
      setOperatingHoursLoading(false);
    }
  };

  const handleOperatingHoursChange = (dayOfWeek: number, field: keyof OperatingHoursApi, value: string | boolean | null) => {
    setOperatingHours((prev) =>
      prev.map((h) => (h.day_of_week === dayOfWeek ? { ...h, [field]: value } : h))
    );
  };

  const handleSaveOperatingHours = async () => {
    try {
      setOperatingHoursSaving(true);

      // 운영시간 안내 문구 저장 (Store.hours)
      const storeResponse = await apiPut(`/api/v1/stores/${store.id}`, {
        hours: storeHoursText || null,
      });
      if (!storeResponse.success) throw new Error(storeResponse.error);

      // 요일별 무인 운영 시간 저장
      const response = await apiPut(`/api/v1/stores/${store.id}/operating-hours`, {
        hours: operatingHours,
      });
      if (!response.success) throw new Error(response.error);

      onUpdate?.();
      alert('운영시간이 저장되었습니다.');
    } catch (error: any) {
      alert(`저장 실패: ${error.message || '알 수 없는 오류'}`);
    } finally {
      setOperatingHoursSaving(false);
    }
  };

  // ==================== 특정일 휴무 ====================
  const loadClosedDates = async () => {
    try {
      setClosedDatesLoading(true);
      const response = await apiGet<ClosedDateItem[]>(`/api/v1/stores/${store.id}/closed-dates`);
      if (response.success && response.data) setClosedDates(response.data);
    } catch (error) {
      console.error('Error loading closed dates:', error);
    } finally {
      setClosedDatesLoading(false);
    }
  };

  const handleAddClosedDate = async () => {
    if (!closedDateForm.start_date) {
      alert('시작일을 선택해주세요.');
      return;
    }
    const endDate = closedDateForm.end_date || closedDateForm.start_date;
    if (endDate < closedDateForm.start_date) {
      alert('종료일은 시작일 이후여야 합니다.');
      return;
    }
    try {
      setClosedDateAdding(true);
      const response = await apiPost(`/api/v1/stores/${store.id}/closed-dates`, {
        start_date: closedDateForm.start_date,
        start_time: closedDateForm.start_time || null,
        end_date: endDate,
        end_time: closedDateForm.end_time || null,
        reason: closedDateForm.reason || null,
      });
      if (!response.success) throw new Error(response.error);
      setClosedDateForm({ start_date: '', start_time: '', end_date: '', end_time: '', reason: '' });
      loadClosedDates();
    } catch (error: any) {
      alert(`등록 실패: ${error.message || '알 수 없는 오류'}`);
    } finally {
      setClosedDateAdding(false);
    }
  };

  const handleDeleteClosedDate = async (id: number) => {
    if (!confirm('이 휴무일을 삭제하시겠습니까?')) return;
    try {
      const response = await apiDelete(`/api/v1/stores/${store.id}/closed-dates/${id}`);
      if (!response.success) throw new Error(response.error);
      loadClosedDates();
    } catch (error: any) {
      alert(`삭제 실패: ${error.message || '알 수 없는 오류'}`);
    }
  };

  const formatDateWithDay = (dateStr: string) => {
    const dateOnly = dateStr.split('T')[0];
    const [y, m, d] = dateOnly.split('-').map(Number);
    const date = new Date(y, m - 1, d);
    const weekdays = ['일', '월', '화', '수', '목', '금', '토'];
    return `${y}.${String(m).padStart(2, '0')}.${String(d).padStart(2, '0')} (${weekdays[date.getDay()]})`;
  };

  const formatHour = (time: string | null) => {
    if (!time) return null;
    const h = parseInt(time.split(':')[0], 10);
    return `${h}시`;
  };

  const formatClosedRange = (cd: ClosedDateItem) => {
    const start = formatDateWithDay(cd.start_date);
    const end = formatDateWithDay(cd.end_date);
    const sameDay = cd.start_date === cd.end_date;
    const stHour = formatHour(cd.start_time);
    const etHour = formatHour(cd.end_time);

    const startStr = stHour ? `${start} ${stHour}` : start;
    const endStr = etHour ? `${end} ${etHour}` : end;

    if (sameDay && !stHour && !etHour) return start; // 종일 하루
    if (sameDay) return `${start} ${stHour || '0시'} ~ ${etHour || '24시'}`;
    return `${startStr} ~ ${endStr}`;
  };

  // ==================== 공지사항 ====================
  const loadNotices = async () => {
    try {
      setNoticesLoading(true);
      const response = await apiGet<Notice[]>(`/api/v1/notices?store_id=${store.id}`);
      if (response.success && response.data) setNotices(response.data);
    } catch (error) {
      console.error('Error loading notices:', error);
    } finally {
      setNoticesLoading(false);
    }
  };

  const handleAddNotice = () => {
    setEditingNotice(null);
    setNoticeForm({ title: '', content: '' });
    setShowNoticeModal(true);
  };

  const handleEditNotice = (notice: Notice) => {
    setEditingNotice(notice);
    setNoticeForm({ title: notice.title, content: notice.content });
    setShowNoticeModal(true);
  };

  const handleSubmitNotice = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const response = editingNotice
        ? await apiPut(`/api/v1/notices/${editingNotice.id}`, noticeForm)
        : await apiPost('/api/v1/notices', { store_id: store.id, ...noticeForm });

      if (!response.success) throw new Error(response.error);
      setShowNoticeModal(false);
      loadNotices();
    } catch (error: any) {
      alert(`저장 실패: ${error.message || '알 수 없는 오류'}`);
    }
  };

  const handleDeleteNotice = async (noticeId: string) => {
    if (!confirm('정말 이 공지사항을 삭제하시겠습니까?')) return;
    try {
      const response = await apiDelete(`/api/v1/notices/${noticeId}`);
      if (!response.success) throw new Error(response.error);
      loadNotices();
    } catch (error: any) {
      alert(`삭제 실패: ${error.message || '알 수 없는 오류'}`);
    }
  };

  // ==================== 룸 설정 ====================
  const loadRooms = async () => {
    try {
      setRoomsLoading(true);
      const response = await apiGet<Room[]>(`/api/v1/rooms?store_id=${store.id}`);
      if (response.success && response.data) {
        setRooms(response.data.sort((a, b) => (a.room_number ?? 0) - (b.room_number ?? 0)));
      }
    } catch (error) {
      console.error('Error loading rooms:', error);
    } finally {
      setRoomsLoading(false);
    }
  };

  const handleRoomInfoSave = async (roomId: string) => {
    const room = rooms.find(r => r.id === roomId);
    if (!room) return;
    try {
      const response = await apiPut(`/api/v1/rooms/${roomId}`, {
        name: room.name,
        facilities: room.facilities,
        enabled: room.enabled !== false,
      });
      if (!response.success) throw new Error(response.error);
      alert('저장되었습니다.');
      setDirtyRoomIds(prev => { const next = new Set(prev); next.delete(roomId); return next; });
      loadRooms();
      onUpdate?.();
    } catch (error: any) {
      alert(`저장 실패: ${error.message}`);
    }
  };

  const handleRoomImageUpload = async (roomId: string, file: File) => {
    if (file.size > 5 * 1024 * 1024) { alert('이미지는 5MB 이하만 가능합니다.'); return; }
    try {
      setRoomImageUploading(roomId);
      const formData = new FormData();
      formData.append('file', file);
      const uploadRes = await fetch(`/api/v1/upload?type=rooms`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` },
        body: formData,
      });
      const uploadData = await uploadRes.json();
      if (!uploadData.success) throw new Error(uploadData.error);

      await apiPut(`/api/v1/rooms/${roomId}`, { image: uploadData.data.url });
      loadRooms();
      onUpdate?.();
    } catch (error: any) {
      alert(`업로드 실패: ${error.message}`);
    } finally {
      setRoomImageUploading(null);
    }
  };

  const handleRoomImageDelete = async (roomId: string) => {
    try {
      await apiPut(`/api/v1/rooms/${roomId}`, { image: null });
      loadRooms();
      onUpdate?.();
    } catch (error: any) {
      alert(`삭제 실패: ${error.message}`);
    }
  };

  // ==================== 룸 배치도 ====================
  const handleRoomLayoutUpload = async (file: File) => {
    if (file.size > 5 * 1024 * 1024) { alert('이미지는 5MB 이하만 가능합니다.'); return; }
    try {
      setRoomLayoutUploading(true);
      const formData = new FormData();
      formData.append('file', file);
      const uploadRes = await fetch(`/api/v1/upload?type=stores`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` },
        body: formData,
      });
      const uploadData = await uploadRes.json();
      if (!uploadData.success) throw new Error(uploadData.error);

      await apiPut(`/api/v1/stores/${store.id}`, { room_layout_image: uploadData.data.url });
      setRoomLayoutImage(uploadData.data.url);
      onUpdate?.();
    } catch (error: any) {
      alert(`업로드 실패: ${error.message}`);
    } finally {
      setRoomLayoutUploading(false);
    }
  };

  const handleRoomLayoutDelete = async () => {
    if (!confirm('룸 배치도를 삭제하시겠습니까?')) return;
    try {
      await apiPut(`/api/v1/stores/${store.id}`, { room_layout_image: null });
      setRoomLayoutImage('');
      onUpdate?.();
    } catch (error: any) {
      alert(`삭제 실패: ${error.message}`);
    }
  };

  // 섹션 토글
  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  return (
    <div className="space-y-4">
      {/* 키오스크 운영 데이터 동기화 */}
      <div className="bg-white rounded-xl shadow-sm border border-teal-100 overflow-hidden">
        <div className="px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-teal-500 rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-loop-left-line text-white text-lg"></i>
            </div>
            <div>
              <h3 className="font-bold text-slate-900">키오스크 동기화</h3>
              <p className="text-sm text-slate-500">
                요금, 공지, 설정, 운영시간을 키오스크에 반영합니다
                {store.kiosk_status?.last_operational_sync_at && (
                  <span className="ml-2 text-slate-400">
                    · 마지막 동기화: {new Date(store.kiosk_status.last_operational_sync_at).toLocaleString('ko-KR')}
                  </span>
                )}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            {syncMessage && (
              <span className={`text-sm font-medium ${syncMessage.type === 'success' ? 'text-teal-600' : 'text-red-500'}`}>
                {syncMessage.type === 'success' ? <i className="ri-check-line mr-1"></i> : <i className="ri-error-warning-line mr-1"></i>}
                {syncMessage.text}
              </span>
            )}
            <button
              onClick={handleSyncOperational}
              disabled={syncLoading || !needsOperationalSync || !store.kiosk_status?.online}
              className={`px-5 py-2.5 text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2 cursor-pointer whitespace-nowrap ${
                needsOperationalSync && store.kiosk_status?.online
                  ? 'bg-teal-500 hover:bg-teal-600'
                  : 'bg-gray-300'
              }`}
              title={!store.kiosk_status?.online ? '키오스크가 오프라인입니다' : !needsOperationalSync ? '이미 최신 상태입니다' : ''}
            >
              <i className={`ri-loop-left-line ${syncLoading ? 'animate-spin' : ''}`}></i>
              {syncLoading ? '동기화 중...' : needsOperationalSync ? '키오스크 동기화' : '동기화 완료'}
            </button>
          </div>
        </div>
      </div>

      {/* 기본 정보 섹션 */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <button
          onClick={() => toggleSection('basicInfo')}
          className="w-full px-6 py-4 flex items-center justify-between bg-gradient-to-r from-cyan-50 to-cyan-100/50 hover:from-cyan-100 hover:to-cyan-100 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-cyan-500 rounded-lg flex items-center justify-center">
              <i className="ri-image-line text-white text-lg"></i>
            </div>
            <div className="text-left">
              <h3 className="font-bold text-slate-900">기본 정보</h3>
              <p className="text-sm text-slate-500">대표 이미지, 소개글</p>
            </div>
          </div>
          <i className={`ri-arrow-${expandedSections.basicInfo ? 'up' : 'down'}-s-line text-xl text-slate-400`}></i>
        </button>

        {expandedSections.basicInfo && (
          <div className="p-6 border-t border-slate-100">
            <div className="flex gap-6">
              {/* 이미지 업로드 */}
              <div className="w-72 flex-shrink-0">
                {imagePreview ? (
                  <div className="relative w-full h-44 bg-slate-100 rounded-lg overflow-hidden">
                    <img src={imagePreview} alt="매장 대표 이미지" className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={handleRemoveImage}
                      className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                    >
                      <i className="ri-delete-bin-line"></i>
                    </button>
                  </div>
                ) : (
                  <div
                    onClick={() => imageInputRef.current?.click()}
                    className="w-full h-44 border-2 border-dashed border-slate-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-cyan-500 transition-colors"
                  >
                    <i className="ri-upload-cloud-line text-4xl text-slate-400 mb-2"></i>
                    <p className="text-sm text-slate-500">클릭하여 업로드</p>
                    <p className="text-xs text-slate-400 mt-1">권장 1200x600 (최대 5MB)</p>
                  </div>
                )}
                <input
                  ref={imageInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="hidden"
                />
              </div>

              {/* 소개글 + 저장 */}
              <div className="flex-1 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">매장 소개글</label>
                  <textarea
                    value={storeDescription}
                    onChange={(e) => setStoreDescription(e.target.value)}
                    placeholder="매장 소개글을 입력하세요 (예: 최신 스크린골프 시스템을 갖춘 프리미엄 매장)"
                    rows={3}
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent resize-none"
                  />
                </div>
                <button
                  onClick={handleSaveBasicInfo}
                  disabled={imageUploading}
                  className="px-6 py-3 bg-cyan-500 text-white font-semibold rounded-lg hover:bg-cyan-600 transition-colors disabled:opacity-50"
                >
                  {imageUploading ? '저장 중...' : '기본 정보 저장'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 공지사항 섹션 */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <button
          onClick={() => toggleSection('notices')}
          className="w-full px-6 py-4 flex items-center justify-between bg-gradient-to-r from-purple-50 to-purple-100/50 hover:from-purple-100 hover:to-purple-100 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-500 rounded-lg flex items-center justify-center">
              <i className="ri-notification-line text-white text-lg"></i>
            </div>
            <div className="text-left">
              <h3 className="font-bold text-slate-900">공지사항</h3>
              <p className="text-sm text-slate-500">{notices.length}개 등록됨</p>
            </div>
          </div>
          <i className={`ri-arrow-${expandedSections.notices ? 'up' : 'down'}-s-line text-xl text-slate-400`}></i>
        </button>

        {expandedSections.notices && (
          <div className="p-6 border-t border-slate-100">
            <div className="flex justify-end mb-4">
              <button
                onClick={handleAddNotice}
                className="px-4 py-2 bg-purple-500 text-white text-sm font-semibold rounded-lg hover:bg-purple-600 transition-colors"
              >
                <i className="ri-add-line mr-1"></i> 공지사항 추가
              </button>
            </div>

            {noticesLoading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
              </div>
            ) : notices.length === 0 ? (
              <div className="text-center py-8 text-slate-500">등록된 공지사항이 없습니다</div>
            ) : (
              <NoticeCarouselAdmin
                notices={notices}
                onEdit={handleEditNotice}
                onDelete={handleDeleteNotice}
              />
            )}
          </div>
        )}
      </div>

      {/* 요금 설정 섹션 */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <button
          onClick={() => toggleSection('pricing')}
          className="w-full px-6 py-4 flex items-center justify-between bg-gradient-to-r from-orange-50 to-orange-100/50 hover:from-orange-100 hover:to-orange-100 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center">
              <i className="ri-price-tag-3-line text-white text-lg"></i>
            </div>
            <div className="text-left">
              <h3 className="font-bold text-slate-900">요금 설정</h3>
              <p className="text-sm text-slate-500">{pricing.length}개 요금제</p>
            </div>
          </div>
          <i className={`ri-arrow-${expandedSections.pricing ? 'up' : 'down'}-s-line text-xl text-slate-400`}></i>
        </button>

        {expandedSections.pricing && (
          <div className="p-6 border-t border-slate-100">
            {/* 시간대 구분 설정 */}
            <div className="mb-6 p-4 bg-orange-50 rounded-lg border border-orange-200">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h4 className="font-semibold text-slate-900">시간대 구분 설정</h4>
                  <p className="text-xs text-slate-500">주간/야간 요금을 구분할 기준 시간을 설정합니다 (예약 페이지에도 반영)</p>
                </div>
              </div>
              <div className="flex items-center gap-4 flex-wrap">
                {/* 구분 없음(종일) 체크박스 */}
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!daytimeSettings.daytime_enabled}
                    onChange={(e) => setDaytimeSettings({ ...daytimeSettings, daytime_enabled: !e.target.checked })}
                    className="w-4 h-4 text-orange-500 rounded border-slate-300 focus:ring-orange-500"
                  />
                  <span className="text-sm font-medium text-slate-700">구분 없음 (종일)</span>
                </label>

                {/* 주간/야간 구분 사용 시에만 시간 선택 표시 */}
                {daytimeSettings.daytime_enabled && (
                  <>
                    <div className="flex items-center gap-2">
                      <label className="text-sm font-medium text-slate-700">주간 시작</label>
                      <select
                        value={daytimeSettings.daytime_start_hour}
                        onChange={(e) => setDaytimeSettings({ ...daytimeSettings, daytime_start_hour: parseInt(e.target.value) })}
                        className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-transparent bg-white"
                      >
                        {HOUR_OPTIONS.map((h) => (
                          <option key={h} value={parseInt(h)}>{h}:00</option>
                        ))}
                      </select>
                    </div>
                    <span className="text-slate-400">~</span>
                    <div className="flex items-center gap-2">
                      <label className="text-sm font-medium text-slate-700">주간 종료</label>
                      <select
                        value={daytimeSettings.daytime_end_hour}
                        onChange={(e) => setDaytimeSettings({ ...daytimeSettings, daytime_end_hour: parseInt(e.target.value) })}
                        className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-transparent bg-white"
                      >
                        {HOUR_OPTIONS.map((h) => (
                          <option key={h} value={parseInt(h)}>{h}:00</option>
                        ))}
                      </select>
                    </div>
                  </>
                )}

                <button
                  onClick={handleSaveDaytimeSettings}
                  disabled={daytimeSettingsSaving}
                  className="px-4 py-2 bg-orange-500 text-white text-sm font-semibold rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50"
                >
                  {daytimeSettingsSaving ? '저장 중...' : '저장'}
                </button>
              </div>
              <p className="mt-2 text-xs text-orange-700">
                <i className="ri-information-line mr-1"></i>
                {daytimeSettings.daytime_enabled
                  ? `현재: 주간 ${String(daytimeSettings.daytime_start_hour).padStart(2, '0')}:00 ~ ${String(daytimeSettings.daytime_end_hour).padStart(2, '0')}:00 | 야간 ${String(daytimeSettings.daytime_end_hour).padStart(2, '0')}:00 ~ 익일 ${String(daytimeSettings.daytime_start_hour).padStart(2, '0')}:00`
                  : '현재: 종일 (주간/야간 구분 없음)'}
              </p>
            </div>

            {/* 게임 라이프사이클 - 사전 준비 시간 */}
            <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h4 className="font-semibold text-slate-900">사전 준비 시간</h4>
                  <p className="text-xs text-slate-500">예약 시작 전 장비(에어컨, 빔프로젝터, 전등)를 미리 켜는 시간</p>
                </div>
              </div>
              <div className="flex items-center gap-4 flex-wrap">
                <div className="flex items-center gap-2">
                  <select
                    value={preWarmMinutes}
                    onChange={(e) => setPreWarmMinutes(parseInt(e.target.value))}
                    className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                  >
                    {[0, 5, 10, 15, 20, 30, 45, 60].map((m) => (
                      <option key={m} value={m}>{m === 0 ? '사용 안 함' : `${m}분 전`}</option>
                    ))}
                  </select>
                </div>
                <button
                  onClick={handleSavePreWarmMinutes}
                  disabled={preWarmSaving}
                  className="px-4 py-2 bg-blue-500 text-white text-sm font-semibold rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50"
                >
                  {preWarmSaving ? '저장 중...' : '저장'}
                </button>
              </div>
              <p className="mt-2 text-xs text-blue-700">
                <i className="ri-information-line mr-1"></i>
                {preWarmMinutes === 0
                  ? '예약 시작 시간에 맞춰 장비가 켜집니다.'
                  : `예약 시작 ${preWarmMinutes}분 전에 자동으로 장비가 켜집니다.`}
              </p>
            </div>

            <div className="flex justify-end mb-4">
              <button
                onClick={handleAddPricing}
                className="px-4 py-2 bg-orange-500 text-white text-sm font-semibold rounded-lg hover:bg-orange-600 transition-colors"
              >
                <i className="ri-add-line mr-1"></i> 요금제 추가
              </button>
            </div>

            {pricingLoading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
              </div>
            ) : pricing.length === 0 ? (
              <div className="text-center py-8 text-slate-500">등록된 요금제가 없습니다</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {pricing.map((item) => {
                  const dayTypeLabel = {
                    all: '매일(기본)',
                    weekday: '평일',
                    weekend: '주말',
                    holiday: '공휴일',
                    custom: '커스텀',
                  }[item.day_type] || item.day_type;

                  const timeSlotLabel = {
                    all_day: '종일',
                    daytime: '주간',
                    nighttime: '야간',
                  }[item.time_slot] || item.time_slot;

                  return (
                    <div key={item.id} className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-bold text-slate-900">{item.display_name || `${dayTypeLabel} ${timeSlotLabel}`}</span>
                        <div className="flex gap-1">
                          <button
                            onClick={() => handleEditPricing(item)}
                            className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                          >
                            <i className="ri-edit-line"></i>
                          </button>
                          <button
                            onClick={() => handleDeletePricing(item.id)}
                            className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                          >
                            <i className="ri-delete-bin-line"></i>
                          </button>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded">
                          {dayTypeLabel}
                        </span>
                        <span className="px-2 py-0.5 text-xs font-medium bg-purple-100 text-purple-700 rounded">
                          {timeSlotLabel}
                        </span>
                      </div>
                      <div className="text-2xl font-bold text-orange-600">{item.price_per_hour.toLocaleString()}원</div>
                      <p className="text-xs text-slate-500">시간당</p>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* 룸 설정 섹션 (룸 배치도 + 룸 리스트 통합) */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <button
          onClick={() => toggleSection('rooms')}
          className="w-full px-6 py-4 flex items-center justify-between bg-gradient-to-r from-teal-50 to-teal-100/50 hover:from-teal-100 hover:to-teal-100 transition-colors cursor-pointer"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-teal-500 rounded-lg flex items-center justify-center">
              <i className="ri-door-line text-white text-lg"></i>
            </div>
            <div className="text-left">
              <h3 className="font-bold text-slate-900">룸 설정</h3>
              <p className="text-sm text-slate-500">룸 배치도, 시설정보, 사용 여부</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-400">{rooms.length}개 룸</span>
            <i className={`ri-arrow-${expandedSections.rooms ? 'up' : 'down'}-s-line text-xl text-slate-400`}></i>
          </div>
        </button>

        {expandedSections.rooms && (
          <div className="p-6 border-t border-slate-100 space-y-6">
            {/* 룸 배치도 */}
            <div>
              <h4 className="text-sm font-semibold text-slate-700 mb-3">
                <i className="ri-layout-grid-line mr-1"></i> 룸 배치도
              </h4>
              {roomLayoutImage ? (
                <div className="relative inline-block">
                  <img src={roomLayoutImage} alt="룸 배치도" className="max-h-48 object-contain rounded-lg border border-slate-200" />
                  <div className="mt-2 flex gap-2">
                    <button
                      onClick={() => roomLayoutInputRef.current?.click()}
                      className="px-3 py-1.5 text-xs bg-slate-100 text-slate-600 rounded-lg hover:bg-slate-200 transition-colors cursor-pointer"
                    >
                      <i className="ri-camera-line mr-1"></i>변경
                    </button>
                    <button
                      onClick={handleRoomLayoutDelete}
                      className="px-3 py-1.5 text-xs bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors cursor-pointer"
                    >
                      <i className="ri-delete-bin-line mr-1"></i>삭제
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => roomLayoutInputRef.current?.click()}
                  className="w-full py-8 border-2 border-dashed border-slate-300 rounded-xl flex flex-col items-center justify-center text-slate-400 hover:text-teal-500 hover:border-teal-300 hover:bg-teal-50/50 transition-all cursor-pointer"
                >
                  {roomLayoutUploading ? (
                    <i className="ri-loader-4-line text-2xl animate-spin"></i>
                  ) : (
                    <>
                      <i className="ri-image-add-line text-2xl mb-1"></i>
                      <span className="text-sm">룸 배치도 이미지 업로드</span>
                      <span className="text-xs text-slate-400 mt-0.5">JPG, PNG (최대 5MB)</span>
                    </>
                  )}
                </button>
              )}
              <input ref={roomLayoutInputRef} type="file" accept="image/*" className="hidden"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) handleRoomLayoutUpload(f); e.target.value = ''; }} />
            </div>

            {/* 룸 리스트 */}
            <div>
              <h4 className="text-sm font-semibold text-slate-700 mb-3">
                <i className="ri-door-open-line mr-1"></i> 룸 목록
              </h4>
              {roomsLoading ? (
                <div className="text-center py-8 text-slate-400">로딩 중...</div>
              ) : rooms.length === 0 ? (
                <div className="text-center py-8 text-slate-500">등록된 룸이 없습니다. 시설 설정에서 룸을 추가하세요.</div>
              ) : (
                <div className="grid grid-cols-3 gap-3">
                  {rooms.map(room => (
                    <div key={room.id} className={`border-2 rounded-xl p-3 ${room.enabled !== false ? 'border-slate-400' : 'border-red-400 bg-red-50/50'}`}>
                      {/* Row 1: 번호 + 이름 + 토글 */}
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-sm font-bold text-teal-600 whitespace-nowrap">{room.room_number}번</span>
                        <input
                          type="text"
                          value={room.name}
                          onChange={(e) => {
                            setRooms(rooms.map(r => r.id === room.id ? { ...r, name: e.target.value } : r));
                            markRoomDirty(room.id);
                          }}
                          className="flex-1 text-sm font-semibold px-2 py-1 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-300 focus:border-teal-400 outline-none min-w-0"
                        />
                        <div
                          onClick={() => {
                            setRooms(rooms.map(r => r.id === room.id ? { ...r, enabled: r.enabled === false ? true : false } as any : r));
                            markRoomDirty(room.id);
                          }}
                          className={`w-10 h-5 rounded-full transition-colors relative cursor-pointer flex-shrink-0 ${room.enabled !== false ? 'bg-emerald-500' : 'bg-slate-300'}`}
                        >
                          <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${room.enabled !== false ? 'translate-x-5' : ''}`} />
                        </div>
                      </div>

                      {/* Row 2: 태그 입력 */}
                      <div className="flex items-center gap-2 mb-2">
                        <i className="ri-price-tag-3-line text-slate-400 text-sm flex-shrink-0"></i>
                        <input
                          type="text"
                          value={room.facilities || ''}
                          placeholder="시설 태그"
                          onChange={(e) => {
                            setRooms(rooms.map(r => r.id === room.id ? { ...r, facilities: e.target.value } : r));
                            markRoomDirty(room.id);
                          }}
                          className="flex-1 text-sm px-2 py-1 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-300 focus:border-teal-400 outline-none"
                        />
                      </div>

                      {/* Row 3: 상태 + 저장 */}
                      <div className="flex items-center justify-between">
                        <span className={`text-xs font-semibold ${room.enabled !== false ? 'text-emerald-700' : 'text-amber-700'}`}>
                          {room.enabled !== false ? '이용 가능' : '사용 중지'}
                        </span>
                        <button
                          onClick={() => handleRoomInfoSave(room.id)}
                          disabled={!dirtyRoomIds.has(room.id)}
                          className={`px-3 py-1 text-xs font-semibold rounded-lg transition-colors cursor-pointer ${dirtyRoomIds.has(room.id) ? 'bg-teal-500 text-white hover:bg-teal-600' : 'bg-slate-100 text-slate-400 cursor-not-allowed'}`}
                        >
                          저장
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* 룸 이미지 기능 제거됨 */}
          </div>
        )}
      </div>

      {/* 운영시간 섹션 */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <button
          onClick={() => toggleSection('operatingHours')}
          className="w-full px-6 py-4 flex items-center justify-between bg-gradient-to-r from-emerald-50 to-emerald-100/50 hover:from-emerald-100 hover:to-emerald-100 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-500 rounded-lg flex items-center justify-center">
              <i className="ri-time-line text-white text-lg"></i>
            </div>
            <div className="text-left">
              <h3 className="font-bold text-slate-900">운영시간</h3>
              <p className="text-sm text-slate-500">요일별 예약 제외 시간</p>
            </div>
          </div>
          <i className={`ri-arrow-${expandedSections.operatingHours ? 'up' : 'down'}-s-line text-xl text-slate-400`}></i>
        </button>

        {expandedSections.operatingHours && (
          <div className="p-6 border-t border-slate-100">
            {operatingHoursLoading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
              </div>
            ) : (
              <>
                {/* 운영시간 안내 문구 (매장 페이지에 표시됨) */}
                <div className="mb-6 p-4 bg-white rounded-lg border border-emerald-200">
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    <i className="ri-store-2-line mr-1"></i>
                    운영시간 안내 문구
                  </label>
                  <input
                    type="text"
                    value={storeHoursText}
                    onChange={(e) => setStoreHoursText(e.target.value)}
                    placeholder="예: 24시간 무인 운영 / 평일 10:00~22:00, 주말 09:00~23:00"
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  />
                  <p className="mt-2 text-xs text-slate-500">
                    <i className="ri-information-line mr-1"></i>
                    이 문구는 매장 상세 페이지의 운영시간 정보에 표시됩니다.
                  </p>
                </div>

                <div className="mb-4 p-4 bg-emerald-50 rounded-lg space-y-2">
                  <p className="text-sm text-emerald-800 font-medium">
                    <i className="ri-information-line mr-1"></i>
                    예약 제외 시간 설정 안내
                  </p>
                  <ul className="text-xs text-emerald-700 space-y-1 ml-5 list-disc">
                    <li><strong>24시간 예약:</strong> 체크 시 해당 요일 24시간 예약 가능</li>
                    <li><strong>일부 제외:</strong> 09:00 ~ 20:00 설정 → 새벽/야간만 예약 가능</li>
                    <li><strong>휴무:</strong> 해당 요일 매장 휴업 (예약 불가)</li>
                  </ul>
                </div>

                <div className="space-y-3">
                  {[...operatingHours]
                    .sort((a, b) => a.day_of_week - b.day_of_week)
                    .map((hours) => {
                      // 24시간 예약 = excluded_start와 excluded_end가 모두 null
                      const is24Hour = !hours.excluded_start && !hours.excluded_end;
                      const startTime = parseTimeString(hours.excluded_start);
                      const endTime = parseTimeString(hours.excluded_end || '20:00');

                      return (
                        <div
                          key={`day-${hours.day_of_week}`}
                          className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg flex-wrap"
                        >
                          <span className="w-12 font-bold text-slate-700">{DAY_NAMES[hours.day_of_week]}</span>

                          {/* 휴무 체크 */}
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={hours.is_closed}
                              onChange={(e) =>
                                handleOperatingHoursChange(hours.day_of_week, 'is_closed', e.target.checked)
                              }
                              className="w-4 h-4 text-red-500 rounded border-slate-300 focus:ring-red-500"
                            />
                            <span className="text-sm text-red-600 font-medium">휴무</span>
                          </label>

                          {/* 24시간 예약 체크 */}
                          {!hours.is_closed && (
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={is24Hour}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    // 24시간 예약: null로 설정
                                    handleOperatingHoursChange(hours.day_of_week, 'excluded_start', null);
                                    handleOperatingHoursChange(hours.day_of_week, 'excluded_end', null);
                                  } else {
                                    // 기본값 설정
                                    handleOperatingHoursChange(hours.day_of_week, 'excluded_start', '09:00');
                                    handleOperatingHoursChange(hours.day_of_week, 'excluded_end', '20:00');
                                  }
                                }}
                                className="w-4 h-4 text-emerald-500 rounded border-slate-300 focus:ring-emerald-500"
                              />
                              <span className="text-sm text-emerald-600 font-medium">24시간</span>
                            </label>
                          )}

                          {/* 제외 시간 설정 (휴무 아니고, 24시간이 아닐 때만) */}
                          {!hours.is_closed && !is24Hour && (
                            <>
                              <span className="text-sm text-slate-500">제외</span>
                              <div className="flex items-center gap-1">
                                <select
                                  value={startTime.hour}
                                  onChange={(e) =>
                                    handleOperatingHoursChange(hours.day_of_week, 'excluded_start', combineTime(e.target.value, startTime.minute))
                                  }
                                  className="px-2 py-1.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-white cursor-pointer"
                                >
                                  {HOUR_OPTIONS.map((h) => (
                                    <option key={h} value={h}>{h}</option>
                                  ))}
                                </select>
                                <span className="text-slate-400">:</span>
                                <select
                                  value={startTime.minute}
                                  onChange={(e) =>
                                    handleOperatingHoursChange(hours.day_of_week, 'excluded_start', combineTime(startTime.hour, e.target.value))
                                  }
                                  className="px-2 py-1.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-white cursor-pointer"
                                >
                                  {MINUTE_OPTIONS.map((m) => (
                                    <option key={m} value={m}>{m}</option>
                                  ))}
                                </select>
                              </div>
                              <span className="text-sm text-slate-500">~</span>
                              <div className="flex items-center gap-1">
                                <select
                                  value={endTime.hour}
                                  onChange={(e) =>
                                    handleOperatingHoursChange(hours.day_of_week, 'excluded_end', combineTime(e.target.value, endTime.minute))
                                  }
                                  className="px-2 py-1.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-white cursor-pointer"
                                >
                                  {HOUR_OPTIONS_END.map((h) => (
                                    <option key={h} value={h}>{h}</option>
                                  ))}
                                </select>
                                <span className="text-slate-400">:</span>
                                <select
                                  value={endTime.minute}
                                  onChange={(e) =>
                                    handleOperatingHoursChange(hours.day_of_week, 'excluded_end', combineTime(endTime.hour, e.target.value))
                                  }
                                  disabled={endTime.hour === '24'}
                                  className="px-2 py-1.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-white cursor-pointer disabled:bg-slate-100 disabled:cursor-not-allowed"
                                >
                                  {MINUTE_OPTIONS.map((m) => (
                                    <option key={m} value={m}>{m}</option>
                                  ))}
                                </select>
                              </div>
                            </>
                          )}

                          {/* 상태 표시 */}
                          {hours.is_closed && (
                            <span className="text-xs text-red-500 bg-red-50 px-2 py-1 rounded">매장 휴업</span>
                          )}
                          {!hours.is_closed && is24Hour && (
                            <span className="text-xs text-emerald-600 bg-emerald-50 px-2 py-1 rounded">24시간 예약 가능</span>
                          )}
                        </div>
                      );
                    })}
                </div>

                <div className="mt-4 flex justify-end">
                  <button
                    onClick={handleSaveOperatingHours}
                    disabled={operatingHoursSaving}
                    className="px-6 py-3 bg-emerald-500 text-white font-semibold rounded-lg hover:bg-emerald-600 transition-colors disabled:opacity-50"
                  >
                    {operatingHoursSaving ? '저장 중...' : '운영시간 저장'}
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* 특정일 휴무 섹션 */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <button
          onClick={() => toggleSection('closedDates')}
          className="w-full px-6 py-4 flex items-center justify-between bg-gradient-to-r from-red-50 to-red-100/50 hover:from-red-100 hover:to-red-100 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-500 rounded-lg flex items-center justify-center">
              <i className="ri-calendar-close-line text-white text-lg"></i>
            </div>
            <div className="text-left">
              <h3 className="font-bold text-slate-900">특정일 휴무</h3>
              <p className="text-sm text-slate-500">
                {closedDates.length > 0 ? `${closedDates.length}일 등록됨` : '명절, 공사 등 특정 날짜 휴무 설정'}
              </p>
            </div>
          </div>
          <i className={`ri-arrow-${expandedSections.closedDates ? 'up' : 'down'}-s-line text-xl text-slate-400`}></i>
        </button>

        {expandedSections.closedDates && (
          <div className="p-6 border-t border-slate-100">
            {/* 추가 폼 */}
            <div className="mb-6 p-4 bg-slate-50 rounded-lg space-y-3">
              <div className="flex flex-wrap items-end gap-3">
                {/* 시작일 */}
                <div className="min-w-[180px]">
                  <label className="block text-xs font-medium text-slate-600 mb-1">시작일</label>
                  <div
                    onClick={() => closedDateStartRef.current?.showPicker?.()}
                    className="relative flex items-center gap-2 px-3 py-2 border border-slate-300 rounded-lg cursor-pointer hover:bg-white transition-colors"
                  >
                    <i className="ri-calendar-line text-slate-400"></i>
                    <span className={`text-sm ${closedDateForm.start_date ? 'font-medium text-slate-700' : 'text-slate-400'}`}>
                      {closedDateForm.start_date ? formatDateWithDay(closedDateForm.start_date) : '시작일 선택'}
                    </span>
                    <input
                      ref={closedDateStartRef}
                      type="date"
                      value={closedDateForm.start_date}
                      onChange={(e) => {
                        const val = e.target.value;
                        setClosedDateForm((prev) => ({
                          ...prev,
                          start_date: val,
                          end_date: prev.end_date && prev.end_date < val ? val : prev.end_date,
                        }));
                      }}
                      className="absolute inset-0 opacity-0 cursor-pointer"
                    />
                  </div>
                </div>

                {/* 시작 시간 */}
                <div className="w-[90px]">
                  <label className="block text-xs font-medium text-slate-600 mb-1">시작 시</label>
                  <select
                    value={closedDateForm.start_time}
                    onChange={(e) => setClosedDateForm((prev) => ({ ...prev, start_time: e.target.value }))}
                    className="w-full px-2 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-red-300 focus:border-red-400 outline-none"
                  >
                    <option value="">종일</option>
                    {Array.from({ length: 24 }, (_, i) => (
                      <option key={i} value={`${String(i).padStart(2, '0')}:00`}>{String(i).padStart(2, '0')}시</option>
                    ))}
                  </select>
                </div>

                <span className="pb-2 text-slate-400 font-medium">~</span>

                {/* 종료일 */}
                <div className="min-w-[180px]">
                  <label className="block text-xs font-medium text-slate-600 mb-1">종료일</label>
                  <div
                    onClick={() => closedDateEndRef.current?.showPicker?.()}
                    className="relative flex items-center gap-2 px-3 py-2 border border-slate-300 rounded-lg cursor-pointer hover:bg-white transition-colors"
                  >
                    <i className="ri-calendar-line text-slate-400"></i>
                    <span className={`text-sm ${closedDateForm.end_date ? 'font-medium text-slate-700' : 'text-slate-400'}`}>
                      {closedDateForm.end_date ? formatDateWithDay(closedDateForm.end_date) : '종료일 선택'}
                    </span>
                    <input
                      ref={closedDateEndRef}
                      type="date"
                      value={closedDateForm.end_date}
                      min={closedDateForm.start_date || undefined}
                      onChange={(e) => setClosedDateForm((prev) => ({ ...prev, end_date: e.target.value }))}
                      className="absolute inset-0 opacity-0 cursor-pointer"
                    />
                  </div>
                </div>

                {/* 종료 시간 */}
                <div className="w-[90px]">
                  <label className="block text-xs font-medium text-slate-600 mb-1">종료 시</label>
                  <select
                    value={closedDateForm.end_time}
                    onChange={(e) => setClosedDateForm((prev) => ({ ...prev, end_time: e.target.value }))}
                    className="w-full px-2 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-red-300 focus:border-red-400 outline-none"
                  >
                    <option value="">종일</option>
                    {Array.from({ length: 24 }, (_, i) => (
                      <option key={i} value={`${String(i).padStart(2, '0')}:00`}>{String(i).padStart(2, '0')}시</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex items-end gap-3">
                <div className="flex-1">
                  <label className="block text-xs font-medium text-slate-600 mb-1">사유 (선택)</label>
                  <input
                    type="text"
                    value={closedDateForm.reason}
                    onChange={(e) => setClosedDateForm((prev) => ({ ...prev, reason: e.target.value }))}
                    placeholder="예: 설날 연휴, 내부 공사, 정기 휴무"
                    maxLength={100}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-red-300 focus:border-red-400 outline-none"
                    onKeyDown={(e) => { if (e.key === 'Enter') handleAddClosedDate(); }}
                  />
                </div>
                <button
                  onClick={handleAddClosedDate}
                  disabled={closedDateAdding || !closedDateForm.start_date}
                  className="px-5 py-2 bg-red-500 text-white text-sm font-semibold rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50"
                >
                  <i className="ri-add-line mr-1"></i>
                  {closedDateAdding ? '등록 중...' : '추가'}
                </button>
              </div>
              <p className="text-xs text-slate-400">
                시간을 비우면 종일 휴무로 적용됩니다. 종료일을 비우면 시작일과 같은 날로 적용됩니다.
              </p>
            </div>

            {/* 목록 */}
            {closedDatesLoading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500"></div>
              </div>
            ) : closedDates.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <i className="ri-calendar-check-line text-3xl text-slate-300 block mb-2"></i>
                등록된 특정일 휴무가 없습니다
              </div>
            ) : (
              <div className="space-y-2">
                {closedDates.map((cd) => {
                  const isPast = new Date(cd.end_date + 'T23:59:59') < new Date();
                  const sameDay = cd.start_date === cd.end_date;
                  const dayCount = sameDay ? 1 : Math.round(
                    (new Date(cd.end_date).getTime() - new Date(cd.start_date).getTime()) / (1000 * 60 * 60 * 24)
                  ) + 1;
                  return (
                    <div
                      key={cd.id}
                      className={`flex items-center justify-between px-4 py-3 rounded-lg border ${
                        isPast ? 'bg-slate-50 border-slate-200 opacity-60' : 'bg-white border-slate-200'
                      }`}
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className={`w-8 h-8 flex-shrink-0 rounded-lg flex items-center justify-center ${
                          isPast ? 'bg-slate-200' : 'bg-red-100'
                        }`}>
                          <i className={`ri-calendar-close-line ${isPast ? 'text-slate-400' : 'text-red-500'}`}></i>
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-medium text-slate-900">{formatClosedRange(cd)}</span>
                            {!sameDay && (
                              <span className="px-1.5 py-0.5 bg-red-50 text-red-500 text-xs rounded">{dayCount}일간</span>
                            )}
                            {isPast && (
                              <span className="px-2 py-0.5 bg-slate-200 text-slate-500 text-xs rounded-full">종료됨</span>
                            )}
                          </div>
                          {cd.reason && (
                            <p className="text-sm text-slate-500 mt-0.5 truncate">{cd.reason}</p>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => handleDeleteClosedDate(cd.id)}
                        className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0"
                        title="삭제"
                      >
                        <i className="ri-delete-bin-line"></i>
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            <p className="mt-4 text-xs text-slate-400">
              <i className="ri-information-line mr-1"></i>
              특정일 휴무는 요일별 운영시간과 별개로 적용됩니다. 해당 날짜에 예약이 차단됩니다.
            </p>
          </div>
        )}
      </div>

      {/* 요금제 모달 */}
      {showPricingModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-6">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-slate-900 mb-6">
              {editingPricing ? '요금제 수정' : '요금제 추가'}
            </h3>
            <form onSubmit={handleSubmitPricing} className="space-y-5">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">요금제 이름 *</label>
                <input
                  type="text"
                  value={pricingForm.display_name}
                  onChange={(e) => setPricingForm({ ...pricingForm, display_name: e.target.value })}
                  required
                  placeholder="예: 평일 주간, 주말 야간"
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>

              {/* 요일 타입 & 시간대 선택 */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">적용 요일 *</label>
                  <select
                    value={pricingForm.day_type}
                    onChange={(e) => setPricingForm({ ...pricingForm, day_type: e.target.value as 'all' | 'weekday' | 'weekend' | 'holiday' | 'custom' })}
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent bg-white"
                  >
                    <option value="all">매일 (기본 요금제)</option>
                    <option value="weekday">평일 (월~금)</option>
                    <option value="weekend">주말 (토~일)</option>
                    <option value="holiday">공휴일</option>
                    <option value="custom">커스텀</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">적용 시간대 *</label>
                  <select
                    value={pricingForm.time_slot}
                    onChange={(e) => setPricingForm({ ...pricingForm, time_slot: e.target.value as 'all_day' | 'daytime' | 'nighttime' })}
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent bg-white"
                  >
                    <option value="all_day">종일</option>
                    <option value="daytime">주간 ({String(daytimeSettings.daytime_start_hour).padStart(2, '0')}~{String(daytimeSettings.daytime_end_hour).padStart(2, '0')}시)</option>
                    <option value="nighttime">야간 ({String(daytimeSettings.daytime_end_hour).padStart(2, '0')}~{String(daytimeSettings.daytime_start_hour).padStart(2, '0')}시)</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">시간당 요금 (원) *</label>
                <input
                  type="number"
                  value={pricingForm.price_per_hour}
                  onChange={(e) => setPricingForm({ ...pricingForm, price_per_hour: parseInt(e.target.value) || 0 })}
                  required
                  min="0"
                  step="1000"
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>

              {/* 적용 시간 안내 */}
              <div className="p-3 bg-orange-50 rounded-lg">
                <p className="text-sm text-orange-800">
                  <i className="ri-information-line mr-1"></i>
                  {pricingForm.time_slot === 'all_day' && '종일: 00:00 ~ 24:00 (하루 전체)'}
                  {pricingForm.time_slot === 'daytime' && `주간: ${String(daytimeSettings.daytime_start_hour).padStart(2, '0')}:00 ~ ${String(daytimeSettings.daytime_end_hour).padStart(2, '0')}:00`}
                  {pricingForm.time_slot === 'nighttime' && `야간: ${String(daytimeSettings.daytime_end_hour).padStart(2, '0')}:00 ~ 다음날 ${String(daytimeSettings.daytime_start_hour).padStart(2, '0')}:00`}
                </p>
              </div>

              <div className="flex gap-3 pt-4 border-t border-slate-200">
                <button
                  type="button"
                  onClick={() => setShowPricingModal(false)}
                  className="flex-1 px-4 py-3 bg-slate-200 text-slate-700 font-semibold rounded-lg hover:bg-slate-300 transition-colors"
                >
                  취소
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-3 bg-orange-500 text-white font-semibold rounded-lg hover:bg-orange-600 transition-colors"
                >
                  {editingPricing ? '저장' : '추가'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 공지사항 모달 */}
      {showNoticeModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-6">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6">
            <h3 className="text-xl font-bold text-slate-900 mb-6">
              {editingNotice ? '공지사항 수정' : '공지사항 추가'}
            </h3>
            <form onSubmit={handleSubmitNotice} className="space-y-5">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">제목 *</label>
                <input
                  type="text"
                  value={noticeForm.title}
                  onChange={(e) => setNoticeForm({ ...noticeForm, title: e.target.value })}
                  required
                  placeholder="공지사항 제목"
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">내용 *</label>
                <textarea
                  value={noticeForm.content}
                  onChange={(e) => setNoticeForm({ ...noticeForm, content: e.target.value })}
                  required
                  rows={6}
                  placeholder="공지사항 내용"
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                />
              </div>
              <div className="flex gap-3 pt-4 border-t border-slate-200">
                <button
                  type="button"
                  onClick={() => setShowNoticeModal(false)}
                  className="flex-1 px-4 py-3 bg-slate-200 text-slate-700 font-semibold rounded-lg hover:bg-slate-300 transition-colors"
                >
                  취소
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-3 bg-purple-500 text-white font-semibold rounded-lg hover:bg-purple-600 transition-colors"
                >
                  {editingNotice ? '저장' : '추가'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
